// Boost Blenz Admin — App Health: the 30-second read, and its five drill-downs (SPEC §5.1–5.6).
//
// Every number is real, from /api/app/insights (worker/appinsights.ts). The overview and the
// drill-downs read the SAME cached payload, so a tile and the page it opens can never disagree.
//
// 🔴 NO TARGETS, NO RED/GREEN JUDGMENT ON A METRIC (SPEC §2). Colour means state — someone needs
// help — never a score. Arrows are direction of travel against the previous period, and the page
// says so. A percentage from fewer than 10 people carries "Too few to trust" and its raw count.
import React from "react";
import {
  useT, Icon, Title, Card, Badge, FIGTREE, DETOX, DETOX_ORDER, STATUS_META, TOO_FEW, tooFew,
  useNarrow, useInsights, Body, Dot, SectionHead, Empty, Note, Pill, LinkBtn, Tabs, RangePicker,
  AppStat, Bars, Columns, Line, Funnel, SkeletonTiles, fmtDay, fmtWhen,
} from "./ui";

type Go = (hash: string) => void;

const HOUR_LABEL = (h: number) => (h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`);

/** "Live database · read-only · updated …" + time window + refresh — shared by every Health page. */
function ContextRow({ ins, range, setRange }: any) {
  const t = useT();
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>
        <Icon name="plug" size={14} color={ins.data?.connected === false ? t.bad : t.good} />
        Tracker database · read-only{ins.data?.generatedAt ? ` · updated ${fmtWhen(ins.data.generatedAt)}` : ""}
      </span>
      <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
        <RangePicker value={range} onChange={setRange} />
        <Pill variant="ghost" size="sm" icon="refresh" onClick={ins.refresh} disabled={ins.revalidating}>{ins.revalidating ? "Refreshing" : "Refresh"}</Pill>
      </div>
    </div>
  );
}

/** Loading / error / not-connected, in that order. Returns null when the data is usable. */
function StateGate({ ins }: any) {
  if (ins.loading && !ins.data) return <SkeletonTiles />;
  if (ins.data?.connected === false)
    return (
      <Card pad={0}>
        <Empty icon="plug" title="Tracker not connected" action={<Pill icon="refresh" onClick={ins.refresh}>Try again</Pill>}>
          The dashboard can't reach the Detox Tracker database right now. Nothing is broken in the app itself — customers are unaffected. The numbers would be stale, so they're hidden rather than shown wrong.
        </Empty>
      </Card>
    );
  if (ins.error || !ins.data)
    return (
      <Card pad={0}>
        <Empty icon="alert" title="Could not load App Health" action={<Pill icon="refresh" onClick={ins.refresh}>Retry</Pill>}>
          Something went wrong reading the tracker database. Try again; if it keeps happening, it's on our side, not the app's.
        </Empty>
      </Card>
    );
  return null;
}

function DrillHeader({ title, sub, go, right }: any) {
  return (
    <div>
      <LinkBtn icon="back" onClick={() => go("#/app/health")} style={{ marginBottom: 10 }}>Health overview</LinkBtn>
      <SectionHead title={title} sub={sub} size={24} right={right} />
    </div>
  );
}

const whereRows = (d: any) =>
  (d.where || []).map((w: any) => ({
    label: `${DETOX[w.stage]?.label ?? w.stage} · ${STATUS_META[w.state]?.[0].toLowerCase() ?? w.state}`,
    value: w.n,
    color: w.state === "detoxing" ? DETOX[w.stage]?.color : "rgba(185,169,255,0.4)",
    filter: w.state === "detoxing" ? "detoxing" : w.state,
  }));

// ================================================================== OVERVIEW
export function HealthOverview({ go, range, setRange }: { go: Go; range: string; setRange: (r: string) => void }) {
  const t = useT();
  const narrow = useNarrow();
  const ins = useInsights(range);
  const gate = StateGate({ ins });
  const d = ins.data;
  const k = d?.kpis;
  const diff = (now: number | null | undefined, prev: number | null | undefined) => (now == null || prev == null ? null : now - prev);
  const unanswered = (d?.questions || []).filter((q: any) => q.answered === "none");
  const queue = d?.needs || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <ContextRow ins={ins} range={range} setRange={setRange} />
      {gate || (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(215px,1fr))", gap: 14 }}>
            <AppStat label="Detoxing today" value={k.detoxing.n} icon="pulse" accent={t.accent} delta={diff(k.detoxing.n, k.detoxing.prev)}
              sub="The size of your active base" meaning="People inside a running detox today." onClick={() => go("#/app/health/people")} />
            <AppStat label="On track" value={k.onTrack.pct ?? "—"} unit="%" icon="check" accent={t.good} delta={diff(k.onTrack.pct, k.onTrack.prevPct)}
              tooFewToTrust={tooFew(k.onTrack.of)} sub={`${k.onTrack.n} of ${k.onTrack.of} ${k.onTrack.of === 1 ? "person" : "people"} counted`}
              meaning="Of those detoxing, the share who took yesterday's dose." onClick={() => go("#/app/health/doses")} />
            <AppStat label="Needs a person" value={k.needsPerson.n} icon="alert" accent={k.needsPerson.n ? t.bad : t.good}
              sub="Reach out to these today" meaning="Gone quiet or stuck, and automation has already tried." onClick={() => go("#/app/customers?filter=needs")} />
            <AppStat label="Finishing each detox" value={k.finishing.pct ?? "—"} unit="%" icon="percent" accent={t.purple} delta={diff(k.finishing.pct, k.finishing.prevPct)}
              tooFewToTrust={tooFew(k.finishing.of)} sub={k.finishing.of ? `${k.finishing.n} of ${k.finishing.of} detoxes that ended in this window` : "No detox ended in this window"}
              meaning="Started a detox and were still logging on its last day." onClick={() => go("#/app/health/people")} />
            <AppStat label="Setup completion" value={k.setup.pct ?? "—"} unit="%" icon="phone" accent={t.warn} delta={diff(k.setup.pct, k.setup.prevPct)}
              tooFewToTrust={tooFew(k.setup.of)} sub={`${k.setup.n} of ${k.setup.of} who asked for a code`}
              meaning="Asked for a login code and got fully set up." onClick={() => go("#/app/health/getting-started")} />
            <AppStat label="Reminders on" value={k.reminders.pct ?? "—"} unit="%" icon="bell" accent={t.purple}
              tooFewToTrust={tooFew(k.reminders.of)} sub={`${k.reminders.n} of ${k.reminders.of} with a plan to follow`}
              meaning="Customers whose phone can actually be reminded." onClick={() => go("#/app/health/reminders")} />
          </div>
          <Body size={12} color={t.muted}>
            Arrows are the change since the previous {d.windowDays >= 3650 ? "period" : `${d.windowDays} days`} — direction only, not a score against a target. Any percentage from fewer than {TOO_FEW} people is marked too few to trust.
          </Body>

          <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "minmax(0,1.55fr) minmax(0,1fr)", gap: 18, alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
              <Card>
                <SectionHead title="Where everyone is right now" sub="Every account, by detox and state. Click a row to see those people."
                  right={<Pill variant="ghost" size="sm" onClick={() => go("#/app/health/people")}>People &amp; progress</Pill>} />
                <WhereEveryone d={d} go={go} />
              </Card>
              <Card>
                <SectionHead title="Getting into the app" sub="People, not attempts, in this window. Someone who requests three codes because two went to spam is one person struggling, not three sign-ups."
                  right={<Pill variant="ghost" size="sm" onClick={() => go("#/app/health/getting-started")}>Getting started</Pill>} />
                <SetupFunnel d={d} />
              </Card>
              <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "repeat(2,minmax(0,1fr))", gap: 14 }}>
                {[
                  { id: "people", title: "People & progress", sub: "Who is detoxing, finishing, dropping off", icon: "users" },
                  { id: "doses", title: "Doses & binder", sub: "Taken, missed, timing, binder by routine", icon: "pill" },
                  { id: "reminders", title: "Reminders", sub: "Who has them on, which get tapped", icon: "bell" },
                  { id: "getting-started", title: "Getting started", sub: "Sign-up funnel and where it leaks", icon: "phone" },
                  { id: "questions", title: "Questions", sub: "What people ask, what we missed", icon: "question" },
                ].map((x) => (
                  <Card key={x.id} pad={16} hoverable onClick={() => go("#/app/health/" + x.id)} style={{ borderRadius: t.tileRadius }}>
                    <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                      <Icon name={x.icon} size={17} color={t.purple} style={{ marginTop: 2 }} />
                      <div>
                        <Title size={14}>{x.title}</Title>
                        <Body size={12} color={t.muted} style={{ marginTop: 4 }}>{x.sub}</Body>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
              <Card>
                <SectionHead title="Needs a person" size={16} sub="The app's own reminders and start nudges ran first. These people still went quiet for 7 days, or something on their account is broken."
                  right={<Badge tone={queue.length ? "bad" : "good"}>{queue.length}</Badge>} />
                {queue.length ? (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {queue.slice(0, 6).map((c: any) => (
                      <button key={c.id} onClick={() => go("#/app/customers/" + c.id)}
                        style={{ display: "flex", gap: 10, alignItems: "flex-start", textAlign: "left", background: "none", border: "none", borderTop: "1px solid " + t.border, padding: "12px 0", cursor: "pointer", width: "100%" }}>
                        <span style={{ marginTop: 6 }}><Dot color={t.bad} /></span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>{c.name}</span>
                          <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 12, color: t.muted, lineHeight: 1.45 }}>{c.needs[0].text}</span>
                          {c.needs.length > 1 && <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, marginTop: 2 }}>+{c.needs.length - 1} more reason{c.needs.length > 2 ? "s" : ""}</span>}
                        </span>
                      </button>
                    ))}
                    <Pill variant="ghost" size="sm" icon="arrowRight" style={{ marginTop: 14, alignSelf: "flex-start" }} onClick={() => go("#/app/customers?filter=needs")}>See all in Customers</Pill>
                  </div>
                ) : (
                  <Empty icon="check" title="Nobody needs chasing">Everyone with a running detox is logging or opening the app, and nobody is stuck.</Empty>
                )}
              </Card>
              <Card>
                <SectionHead title="Questions we couldn't answer" size={16} sub="The FAQ to-do list — asked in the app's Help and not answered."
                  right={<Pill variant="ghost" size="sm" onClick={() => go("#/app/health/questions")}>All questions</Pill>} />
                {unanswered.length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                    {unanswered.slice(0, 4).map((q: any, i: number) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderTop: i ? "1px solid " + t.border : "none", paddingTop: i ? 11 : 0 }}>
                        <Body size={13}>{q.question}</Body>
                        <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.warn, whiteSpace: "nowrap" }}>{q.asks}×</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Empty icon="check" title="Nothing unanswered">Every question asked in the app in this window got an answer. Questions are recorded from Sep 13, 2026.</Empty>
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function WhereEveryone({ d, go }: any) {
  const rows = whereRows(d);
  const extra = [
    d.finishedCount ? { label: "Finished the plan", value: d.finishedCount, color: "#9b91c4", filter: "finished" } : null,
    d.neverCount ? { label: "Never set up", value: d.neverCount, color: "#ff8b7d", filter: "never" } : null,
  ].filter(Boolean);
  const all = [...rows, ...extra];
  return all.length ? (
    <Bars rows={all} showPct={false} onRow={(r: any) => go("#/app/customers?filter=" + r.filter)} />
  ) : (
    <Empty title="No accounts yet">Nobody has signed into the tracker yet.</Empty>
  );
}

function SetupFunnel({ d }: any) {
  const gs = d.gettingStarted;
  if (!gs.instrumented) return <Empty icon="phone" title="No sign-ups recorded yet">Nobody has asked for a login code in this window.</Empty>;
  return <Funnel steps={gs.steps.map((s: any) => ({ label: s.label, value: s.n }))} biggestLeak={gs.leak} />;
}

// ================================================================== PEOPLE & PROGRESS
export function PeopleProgress({ go, range, setRange }: any) {
  const t = useT();
  const ins = useInsights(range);
  const gate = StateGate({ ins });
  const d = ins.data;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <ContextRow ins={ins} range={range} setRange={setRange} />
      <DrillHeader go={go} title="People & progress" sub="Where everyone is, and the day people give up. The steep part of each curve is where the program loses them." />
      {gate || (
        <>
          <Card>
            <SectionHead title="Where everyone is right now" size={16} sub="Click a row to see those people." />
            <WhereEveryone d={d} go={go} />
          </Card>
          <Card>
            <SectionHead title="The day people drop off" size={16}
              sub="How many are still logging doses on each day of a detox. Each line starts with everyone who began that detox; someone part-way through only counts up to today." />
            {Object.keys(d.people.dropoff).length ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 22 }}>
                {DETOX_ORDER.filter((s) => d.people.dropoff[s]).map((s) => {
                  const arr = d.people.dropoff[s] as { day: number; still: number; eligible: number }[];
                  const data = arr.map((x) => ({ label: "Day " + x.day, value: x.still }));
                  let worst = { day: 0, drop: 0 };
                  arr.forEach((x, i) => { if (i && arr[i - 1].still - x.still > worst.drop) worst = { day: x.day, drop: arr[i - 1].still - x.still }; });
                  const last = arr[arr.length - 1];
                  return (
                    <div key={s}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                        <Dot color={DETOX[s].color} /><Title size={14}>{DETOX[s].label}</Title>
                        <Badge tone="neutral">{arr[0].eligible} started</Badge>
                        {tooFew(arr[0].eligible) && <Badge tone="neutral">Too few to trust</Badge>}
                      </div>
                      <Line data={data} color={DETOX[s].color} height={120} />
                      <Body size={12} color={t.muted} style={{ marginTop: 8 }}>
                        {last.eligible ? `${last.still} of ${last.eligible} who reached the last day were still logging.` : "Nobody has reached the last day yet."}{" "}
                        {worst.drop ? `Steepest fall: day ${worst.day}, ${worst.drop} ${worst.drop === 1 ? "person" : "people"}.` : "No single-day fall yet."}
                      </Body>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty icon="calendar" title="Nobody has started a detox">The curves appear once someone logs a first dose.</Empty>
            )}
          </Card>
          <Card>
            <SectionHead title="Finished the whole program" size={16} sub="Everyone who owns a plan: done with all of it, or still going." />
            <Bars showPct={false} rows={[
              { label: "Finished", value: d.people.programs.completed, color: t.good },
              { label: "Still going", value: d.people.programs.going, color: t.purple },
            ]} />
          </Card>
        </>
      )}
    </div>
  );
}

// ================================================================== DOSES & BINDER
export function DosesBinder({ go, range, setRange }: any) {
  const t = useT();
  const ins = useInsights(range);
  const gate = StateGate({ ins });
  const d = ins.data;
  const hours = d?.doses?.hours as number[] | undefined;
  // Morning-weighted columns: 4am–11am one by one, then "12p+" — where doses actually happen.
  const hourCols = hours
    ? [...Array.from({ length: 8 }, (_, i) => ({ label: HOUR_LABEL(i + 4), value: hours[i + 4] })), { label: "12p+", value: hours.slice(12).reduce((a, b) => a + b, 0) + hours.slice(0, 4).reduce((a, b) => a + b, 0) }]
    : [];
  const allFaint = d?.doses?.binderByRoutine?.every((r: any) => r.of < TOO_FEW);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <ContextRow ins={ins} range={range} setRange={setRange} />
      <DrillHeader go={go} title="Doses & binder" sub="What actually got taken, when, and which morning routines keep up with the binder." />
      {gate || (
        <>
          <Card>
            <SectionHead title="Doses logged per day" size={16} sub="Taken doses (detox and binder), in this window." />
            {d.doses.series.some((x: any) => x.taken || x.missed) ? (
              <Line data={d.doses.series.map((x: any) => ({ label: fmtDay(x.d), value: x.taken }))} color={t.good} height={150} />
            ) : (
              <Empty icon="pill" title="No doses in this window">Nothing was logged. Try a longer window.</Empty>
            )}
          </Card>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 18 }}>
            <Card>
              <SectionHead title="When people take the morning detox" size={16} sub="Their own local time." />
              {hourCols.some((c) => c.value) ? <Columns rows={hourCols} color={t.accent} /> : <Empty icon="clock" title="No detox doses yet">Nothing to place on the clock.</Empty>}
            </Card>
            <Card>
              <SectionHead title="How doses get logged" size={16} sub="A lot of “filled in later” means the reminder isn't landing at the right moment." />
              {d.doses.bySource.length ? (
                <Bars showPct={false} rows={d.doses.bySource.map((r: any) => ({ label: r.label, value: r.n, color: r.source === "retro" ? t.warn : r.source === "notification" ? t.purple : t.good }))} />
              ) : (
                <Empty icon="pill" title="Nothing logged yet">No taken doses in this window.</Empty>
              )}
            </Card>
          </div>
          <Card>
            <SectionHead title="Which morning routines keep up with the binder" size={16}
              sub="Of the binder days each group has had so far, how many actually got logged. This is about whether the binder gets taken on schedule — not whether it works better for anyone." />
            {d.doses.binderByRoutine.length ? (
              <Bars rows={d.doses.binderByRoutine.map((r: any) => ({
                label: r.label, value: r.taken, display: `${r.taken} of ${r.of}`, pct: r.of ? Math.round((r.taken / r.of) * 100) : null,
                color: t.purple, faded: r.of < TOO_FEW,
              }))} max={Math.max(...d.doses.binderByRoutine.map((r: any) => r.of), 1)} />
            ) : (
              <Empty icon="pill" title="No binder days yet">Nobody who owns the binder has reached a binder day.</Empty>
            )}
            <Note icon="alert" label="What this can and cannot tell you" style={{ marginTop: 16 }}>
              Groups under {TOO_FEW} binder days are drawn faded and their percentage is unreliable.{" "}
              {allFaint ? "Right now every group is below that line — treat this chart as plumbing, not an answer." : "A group that logs more binder days keeps to the schedule better. Nothing here measures an outcome."}{" "}
              Morning routine is recorded from Sep 13, 2026; anyone who hasn't opened a newer build yet shows as “Hasn't told us”.
            </Note>
          </Card>
        </>
      )}
    </div>
  );
}

// ================================================================== REMINDERS
export function Reminders({ go, range, setRange }: any) {
  const t = useT();
  const ins = useInsights(range);
  const gate = StateGate({ ins });
  const r = ins.data?.reminders;
  const pctOf = (n: number, of: number) => (of ? Math.round((n / of) * 100) : "—");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <ContextRow ins={ins} range={range} setRange={setRange} />
      <DrillHeader go={go} title="Reminders" sub="Reminders are what keep people on schedule. Two things have to be true: their switch is on, and their phone can receive a push. Reminders pause between detoxes by design." />
      {gate || (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(215px,1fr))", gap: 14 }}>
            <AppStat label="Reminders switched on" value={pctOf(r.on, r.total)} unit="%" accent={t.good} icon="bell" tooFewToTrust={tooFew(r.total)}
              sub={`${r.on} of ${r.total} with a plan to follow`} meaning="Their own switch, in the app." />
            <AppStat label="Phone can receive a push" value={pctOf(r.canPush, r.total)} unit="%" accent={t.purple} icon="phone" tooFewToTrust={tooFew(r.total)}
              sub={`${r.canPush} of ${r.total}`} meaning="Allowed notifications, and the address is still valid." />
            <AppStat label="Doses logged within an hour of the reminder" value={r.withinHour.pct ?? "—"} unit="%" accent={t.accent} icon="clock" tooFewToTrust={tooFew(r.withinHour.of)}
              sub={`${r.withinHour.n} of ${r.withinHour.of} morning doses · rough signal only`} meaning="Did the reminder actually do the work?" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 18 }}>
            <Card>
              <SectionHead title="Most common reminder times" size={16} sub="Their own local time." />
              {r.times.length ? <Bars showPct={false} rows={r.times.map((x: any) => ({ label: x.label, value: x.n, color: t.purple }))} /> : <Empty icon="clock" title="No reminder times yet">Nobody with a plan has set one.</Empty>}
            </Card>
            <Card>
              <SectionHead title="Which reminders get tapped" size={16} sub="A reminder nobody taps is one to reword. Taps are recorded from Sep 13, 2026." />
              {r.taps.length ? <Bars showPct={false} rows={r.taps.map((x: any) => ({ label: x.label, value: x.n, color: t.accent }))} /> : <Empty icon="bell" title="No taps recorded in this window">Nobody tapped a notification yet — or their build is older than Sep 13.</Empty>}
            </Card>
          </div>
          <Card>
            <SectionHead title="Silent while detoxing" size={16} sub="A running detox with no way to remind them. These are the fixable ones."
              right={<Pill variant="ghost" size="sm" onClick={() => go("#/app/customers?filter=needs")}>Open in Customers</Pill>} />
            {r.silent.length ? r.silent.map((c: any, i: number) => (
              <button key={c.id} onClick={() => go("#/app/customers/" + c.id)} style={{ display: "flex", justifyContent: "space-between", gap: 12, width: "100%", textAlign: "left", background: "none", border: "none", borderTop: i ? "1px solid " + t.border : "none", padding: "11px 0", cursor: "pointer" }}>
                <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: t.fg }}>{c.name}</span>
                <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.bad }}>{c.why}</span>
              </button>
            )) : <Empty icon="check" title="Everyone detoxing can be reminded">No action needed here.</Empty>}
          </Card>
          <Note icon="info" label="What we can't see yet">
            The app tells us when a reminder is tapped, not when it's delivered. A silent customer who never got the reminder and one who ignored it look the same here. Push delivery receipts would separate them.
          </Note>
        </>
      )}
    </div>
  );
}

// ================================================================== GETTING STARTED
export function GettingStarted({ go, range, setRange }: any) {
  const t = useT();
  const ins = useInsights(range);
  const gate = StateGate({ ins });
  const gs = ins.data?.gettingStarted;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <ContextRow ins={ins} range={range} setRange={setRange} />
      <DrillHeader go={go} title="Getting started" sub="Every step from typing an email to being set up, counted per person. Three tries by one struggling person is one person, not three." />
      {gate || (
        <>
          <Card><SectionHead title="The setup funnel" size={16} /><SetupFunnel d={ins.data} /></Card>
          <Card>
            <SectionHead title="What “told us what they have” means" size={16} />
            <Body>
              A customer who bought from the store is matched on their email at sign-up, and the app claims their products for them — they never see this step. It only appears for the people the match couldn't cover: a <strong style={{ color: t.fg }}>gift recipient</strong>, someone who ordered with a different email than they sign in with, or an order the lookup couldn't find. Those people have to tell the app what they own before anything can be scheduled.
            </Body>
            <Note icon="wrench" label="So a leak here is not a customer problem" style={{ marginTop: 14 }}>
              It's an email-matching problem on our side. Find their order in Shopify and add their products from their profile (Fix something → Add products). Everyone stuck after creating an account is in <strong style={{ color: t.fg }}>Customers → Never set up</strong>{gs.neverSetUp.length ? ` — ${gs.neverSetUp.length} right now` : ""}.
            </Note>
            <Pill variant="ghost" size="sm" icon="arrowRight" style={{ marginTop: 14 }} onClick={() => go("#/app/customers?filter=never")}>See who is stuck</Pill>
          </Card>
          <Card>
            <SectionHead title="Why setup failed" size={16} sub="People we failed, in this window. Their email is kept for 90 days so support can help." />
            {gs.failures.some((f: any) => f.n) ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {gs.failures.filter((f: any) => f.n).map((f: any, i: number) => (
                  <div key={f.id} style={{ borderTop: i ? "1px solid " + t.border : "none", padding: "12px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                      <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>{f.label}</span>
                      <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.bad }}>{f.n}</span>
                    </div>
                    <Body size={12.5} color={t.muted} style={{ marginTop: 3 }}>{f.fix}</Body>
                    {f.emails.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                        {f.emails.slice(0, 12).map((e: string) => <Badge key={e} tone="neutral" style={{ textTransform: "none", letterSpacing: 0 }}>{e}</Badge>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon="check" title="No setup failures in this window">Nobody hit a failed code, a missing order or a lookup outage.</Empty>
            )}
          </Card>
          <Card>
            <SectionHead title="Stuck at the door" size={16} sub="Typed their email into the sign-in screen and never got an account. They aren't customers in the tracker, so they don't appear in Customers — this is the only list of them." />
            {gs.stuck.length ? gs.stuck.slice(0, 25).map((s: any, i: number) => (
              <div key={s.email} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderTop: i ? "1px solid " + t.border : "none", padding: "10px 0", flexWrap: "wrap" }}>
                <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: t.fg }}>{s.email}</span>
                <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>{stepWords(s.lastStep)} · {s.attempts} {s.attempts === 1 ? "try" : "tries"} · {s.daysSince === 0 ? "today" : `${s.daysSince}d ago`}</span>
              </div>
            )) : <Empty icon="check" title="Nobody stuck">Everyone who started signing in got an account.</Empty>}
          </Card>
        </>
      )}
    </div>
  );
}
function stepWords(step: string) {
  return ({
    "auth.code_requested": "Asked for a code, never entered it",
    "auth.code_send_failed": "Our code email failed",
    "auth.code_failed": "Entered a wrong code",
    "auth.code_verified": "Code accepted, no account",
    "auth.no_order_match": "No order found",
    "auth.match_unavailable": "Lookup was down",
    "auth.order_matched": "Order found, no account",
  } as Record<string, string>)[step] ?? step;
}

// ================================================================== QUESTIONS
export function Questions({ go, range, setRange }: any) {
  const t = useT();
  const ins = useInsights(range);
  const gate = StateGate({ ins });
  const [tab, setTab] = React.useState("all");
  const qs = ins.data?.questions || [];
  const unans = qs.filter((q: any) => q.answered === "none");
  const src = tab === "unanswered" ? unans : qs;
  const label: Record<string, [string, string]> = { ai: ["Answered by AI", "purple"], faq: ["Answered from the FAQ", "good"], none: ["Not answered", "bad"] };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <ContextRow ins={ins} range={range} setRange={setRange} />
      <DrillHeader go={go} title="Questions" sub="What customers type into the app's Help. The unanswered list is your FAQ to-do list — and every one of them has a named person behind it."
        right={<Tabs value={tab} onChange={setTab} items={[{ id: "all", label: "All", count: qs.length }, { id: "unanswered", label: "Unanswered", count: unans.length }]} />} />
      {gate || (
        <>
          <Note icon="alert" tone="warn" label="What happens when the app cannot answer">
            Nobody is left hanging. The app offers to send the question to the team, which arrives in <strong style={{ color: t.fg }}>Help Desk</strong> as an email titled “Detox Tracker question from …” — counted here as <em>Sent to the team</em>. If they don't send it, the question still lands here with their name on it, so you can reach out first.
          </Note>
          <Card>
            {src.length ? src.map((q: any, i: number) => (
              <div key={i} style={{ borderTop: i ? "1px solid " + t.border : "none", padding: "14px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 220, flex: 1 }}>
                    <Body size={14} style={{ color: t.fg }}>{q.question}</Body>
                    <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <Badge tone={label[q.answered]?.[1] ?? "neutral"}>{label[q.answered]?.[0] ?? q.answered}</Badge>
                      {q.escalated > 0 && <Badge tone="warn">Sent to the team</Badge>}
                      <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>asked {q.asks}× · last {fmtDay(q.lastAt)}</span>
                    </div>
                  </div>
                  {q.answered === "none" && <Pill size="sm" variant="soft" tone="accent" icon="doc" onClick={() => go("#/kb")}>Add to Knowledge Base</Pill>}
                </div>
                {(q.answered === "none" || tab === "unanswered") && (
                  <div style={{ marginTop: 12, background: t.appBg, border: "1px solid " + t.border, borderRadius: 10, padding: "11px 13px" }}>
                    <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 10, letterSpacing: "0.09em", textTransform: "uppercase", color: t.muted, marginBottom: 8 }}>Who asked it</div>
                    {q.askers.some((a: any) => a.name) ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {q.askers.filter((a: any) => a.name).map((a: any) => (
                          <button key={a.id} onClick={() => go("#/app/customers/" + a.id)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: t.surface2, border: "1px solid " + t.border, borderRadius: 999, padding: "5px 12px", cursor: "pointer", fontFamily: FIGTREE, fontWeight: 600, fontSize: 12.5, color: t.fg }}>
                            {a.name}<Icon name="arrowRight" size={12} color={t.purple} />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <Body size={12.5} color={t.muted}>Asked by {q.asks === 1 ? "someone" : "people"} whose account has since been deleted — no one left to reply to.</Body>
                    )}
                  </div>
                )}
              </div>
            )) : (
              <Empty icon="question" title={tab === "unanswered" ? "Nothing unanswered" : "No questions in this window"}>
                Questions typed into the app's Help are recorded from Sep 13, 2026. Try a longer window.
              </Empty>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
